import { IEventModule } from "@Modules/eventPage/root/IEventModule";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNeedUserInputToProceed } from "@Modules/eventPage/utils/useNeedUserInputToProceed";
import {
    FETCH_STATUS,
    useData,
    useEventDataOfSelectedEvent,
    useEventFetched,
    useModal,
    useRenderControl,
} from "@Utils";
import { appendURLSearchParams } from "repoV2/utils/urls&routing/urlParams";
import { useRouter } from "next/router";
import { usePlanDropdownBaseProps } from "@Modules/eventPage/utils/plan";
import { IEvent } from "@Interfaces";
import { IPlanDropdown } from "../IPlanDropdown";
import { useIsOfferingSelectedInPlan } from "./usePlanLeafSelected";
import { useFetchPlanData } from "./useFetchPlanData";

const rootKey: string = "Select a Plan";

// return [categoryName, categoryTitle]
// label might be tilde(~) separated,
// left side contains category
// right side contains category title which will shown as dropdown title
const getLabel = (value?: string) => {
    const [categoryName = "", categoryTitle] = value
        ? value.split("~").map(v => v.trim())
        : [];

    return {
        categoryName,
        categoryTitle,
    };
};

type IPlanDropdownContextValue = {
    hideComponent: boolean;
    selectionPath: string[];
    rootKey: string;
    selectedEvent: IEvent.ISelectedEvent;
    handleChange: (param: {
        val: IPlanDropdown.OptionType | null;
        depth: number;
        isLeaf: boolean;
    }) => void;
    getDropdownSelectorsProps: (depthIndex: number) => {
        isLeaf: boolean;
        value: IPlanDropdown.OptionType | undefined;
        options: IPlanDropdown.OptionType[];
        categoryName: any;
        categoryTitle: any;
    };

    selectionPathToRender: string[];
    loading: boolean;
    planMetadata?: {
        default_plan_text?: string | undefined;
        show_recommended_tag?: boolean;
    };
};

const initialContextValue: IPlanDropdownContextValue = {
    hideComponent: true,
    selectionPath: [],
    rootKey,
    selectedEvent: "",
    handleChange: () => {},
    getDropdownSelectorsProps: () => ({
        isLeaf: false,
        value: undefined,
        options: [],
        categoryName: undefined,
        categoryTitle: undefined,
    }),
    selectionPathToRender: [],
    loading: false,
};

const PlanDropdownContext = createContext(initialContextValue);

export function PlanDropdownContextProvider({
    children,
    urlQueryParams,
    isOpenedAsEventModal,
}: {
    children: React.ReactNode;
    urlQueryParams: IEventModule.IProps["urlQueryParams"];
    isOpenedAsEventModal?: boolean;
}) {
    useFetchPlanData({ urlQueryParams });

    const { eventData, selectedEventUuid } = useEventDataOfSelectedEvent();

    const { hideComponent, planListings, planMetadata, planFetchStatus } =
        usePlanDropdownBaseProps({
            urlQueryParams,
        });

    /**
     * we need `selectionPath` and `listingTree` in reducer cause we might change routes when a dropdown item is slected
     */
    const {
        data: selectionPath = [rootKey],
        setData: setSelectionPath,
        deleteData: deleteSelectionPath,
    } = useData<string[]>("selectionPath", [rootKey]); // rootKey is the entry node of the tree. Has been kept to account for flat plan structures
    const {
        data: listingTree = {},
        setData: setListingTree,
        deleteData: deleteListingTree,
    } = useData<Object>("listingTree", {});

    const {
        setData: setSelectedEventIdInPlan,
        data: selectedEventIdInPlan,
        deleteData: deleteSelectedEventIdInPlan,
    } = useData<string>("selectedEventIdInPlan", eventData.uuid);

    const [categoryTitlesPaths, setCategoryTitlesPaths] = useState<{
        [key: string]: any;
    }>({});

    const router = useRouter();

    const { enableRender, disableRender } = useRenderControl();

    const {
        setModalInfo,
        modalInfo: { modalProps },
    } = useModal();

    // now we are getting "plan_listing_map_uuid" instead of uuid
    const listingLabels = Object.assign(
        {},
        ...(planListings || [])?.map(p => ({
            [p.plan_listing_map_uuid]: {
                label: p.title,
                is_recommended: p.is_recommended,
            },
        }))
    );

    const selectionPathToRender = (selectionPath || [rootKey]).filter(
        s => s !== selectedEventUuid // Don't render a dropdown after the event leaf node has been selected
    );

    const loading = planFetchStatus !== FETCH_STATUS.FETCHED;

    const {
        isOfferingSelectedInPlan,
        setIsOfferingSelectedInPlan,
        deleteIsOfferingSelectedInPlan,
    } = useIsOfferingSelectedInPlan();

    const onOfferingSelectedInPlan = (_isOfferingSelectedInPlan: boolean) => {
        if (_isOfferingSelectedInPlan !== isOfferingSelectedInPlan) {
            // used setTimeout to avoid race condition between other `useData` dispatches like `setEvent`
            setTimeout(() => {
                setIsOfferingSelectedInPlan(_isOfferingSelectedInPlan);
            }, 0);
        }
    };

    const { dontAllowUserToProceed, allowUserToProceed } =
        useNeedUserInputToProceed();

    useEffect(() => {
        if (!hideComponent) {
            // Set the event ID on render if it is not already set
            // This component may re-render if the event needs to be fetched, so this if check is necessary
            if (!selectedEventIdInPlan) {
                setSelectedEventIdInPlan(eventData.uuid);
            }

            if (!isOfferingSelectedInPlan) {
                dontAllowUserToProceed({
                    messageForUserToProceed:
                        "Please select an offering before proceeding",
                });
            }

            // reset the data in redux if event page is open as a modal
            return () => {
                if (isOpenedAsEventModal) {
                    deleteIsOfferingSelectedInPlan();
                    deleteSelectedEventIdInPlan();
                    deleteSelectionPath();
                    deleteListingTree();
                }
            };
        }

        return () => {};
    }, [hideComponent]);

    useEffect(() => {
        if (!planListings) return;

        // Since this can be a heavy computations, this has been made to run asynchronously, and the `rendered` state is set to true in the end
        (async () => {
            const tree: any = {};
            const path: any = {};

            const categories = planListings?.map(p => [
                p.plan_listing_map_uuid,
                `${rootKey},${p.categories || ""}`
                    .split(",")
                    .map(v => v.trim())
                    .filter(v => v),
            ]);

            categories.forEach(([plan_listing_map_uuid, category]) => {
                let parentRef = tree;
                let titlePath = path;

                for (let depth = 0; depth < category.length; depth += 1) {
                    const { categoryName, categoryTitle } = getLabel(
                        category[depth]
                    );

                    if (depth === category.length - 1) {
                        // If the end of the path has been hit
                        if (
                            // Either the node does not exist
                            !(categoryName in parentRef) ||
                            // Or it is already an endpoint with a listing, so checking
                            // for an object but not an array (Arrays are considered objects)
                            (typeof parentRef[categoryName] === "object" &&
                                !Array.isArray(parentRef[categoryName]))
                        ) {
                            // If it is either nonexistent or is a non-leaf node, overwrite it with an array with the listing on that new leaf
                            parentRef[categoryName] = [plan_listing_map_uuid];
                        } else {
                            // If there is already a leaf array with listings, just add to it
                            parentRef[categoryName].push(plan_listing_map_uuid);
                        }
                    } else if (!(categoryName in parentRef)) {
                        // If the child node does not exist while the path is being traversed
                        parentRef[categoryName] =
                            depth === category.length - 1
                                ? [plan_listing_map_uuid]
                                : {};

                        titlePath[categoryName] = {
                            categoryName,
                            categoryTitle:
                                categoryTitle ||
                                titlePath[categoryName]?.categoryTitle,
                        };
                    }

                    if (categoryName in parentRef) {
                        titlePath[categoryName] = {
                            ...titlePath[categoryName],
                            categoryName,
                            categoryTitle:
                                titlePath[categoryName]?.categoryTitle ||
                                categoryTitle,
                        };
                    }

                    parentRef = parentRef[categoryName];
                    titlePath = titlePath[categoryName];
                }
            });
            if (JSON.stringify(tree) !== JSON.stringify({})) {
                setListingTree(tree);
            }
            if (JSON.stringify(path) !== JSON.stringify({})) {
                setCategoryTitlesPaths(path);
            }
        })();
    }, [planListings]);

    /**
     * This hook will take care of auto-fetching the event data if the event ID passed to it is changed
     */
    useEventFetched({
        eventId: selectedEventIdInPlan,
        setSelected: !hideComponent,
        attemptFetch: !hideComponent,
        forceUsePropsEventId: !hideComponent,
        callbackFn: () => {
            setTimeout(() => {
                enableRender();
                // In case the event is already fetched, the disableRender and enableRender
                //  will cause the screen to flash quickly, which is bad for UX (don't want
                // to give out free epilepsy tests). So, a buffer loading time is added.
            }, 300);

            if (isOpenedAsEventModal) {
                setModalInfo({
                    modalProps: {
                        ...modalProps,
                        eventId: selectedEventIdInPlan,
                    },
                });
            }

            const {
                eventId: oldEventId,
                _UCR_return_href: unusedVal,
                plan_id,
                planSlug,
                // This is to remove this from otherParams.
                // This is from next-contextual-routing package,
                // which needs to be phased out
                ...otherParams
            } = router.query as {
                [k: string]: string;
            };
            // Using the Next JS router glitches and gets stuck in a loop in the case of a modal,
            // so this is the last resort. Implementation of the intended way is welcome.
            if (planSlug) {
                window.history.pushState(
                    window.history.state,
                    "", // This param is currently unused anyways
                    appendURLSearchParams(`${window.location.pathname}`, {
                        ...otherParams,
                        ...{ plan_event_id: selectedEventIdInPlan },
                    })
                );
            }
        },
    });

    // Returns a copy of the subtree needed for rendering a dropdown
    const getTreeRef = (selectionProp: string[]): Object | false => {
        let rootRef = { ...listingTree };

        // The code below has two purposes:
        // - traverses the tree and gets to the requested node
        // - checks if the path provided is valid
        // If the path is invalid, an
        const isValid = selectionProp.every(s => {
            if (s in rootRef) {
                rootRef = rootRef[s as keyof typeof rootRef];
                return true;
            }
            return false;
        });
        return isValid && rootRef;
    };

    const getTreeTitle = (
        selectionProp: string[]
    ): { [key: string]: any } | false => {
        let rootRef = { ...categoryTitlesPaths };

        // The code below has two purposes:
        // - traverses the tree and gets to the requested node
        // - checks if the path provided is valid
        // If the path is invalid, returns false
        const isValid = selectionProp.every(s => {
            if (s in rootRef) {
                rootRef = rootRef[s];
                return true;
            }
            return false;
        });
        return isValid && rootRef;
    };

    const handleChange = ({
        val,
        depth: depthProp,
        isLeaf,
    }: {
        val: IPlanDropdown.OptionType | null;
        depth: number;
        isLeaf: boolean;
    }) => {
        if (!val?.value) return;

        onOfferingSelectedInPlan(isLeaf);

        const newPath = [...selectionPath.slice(0, depthProp + 1)];
        if (isLeaf) {
            allowUserToProceed();
            disableRender();
            setSelectedEventIdInPlan(val?.value);
        }
        setSelectionPath([...newPath, val?.value]);
    };

    const getDropdownSelectorsProps = (depthIndex: number) => {
        const path = selectionPath.slice(0, depthIndex + 1); // Path to this node
        const options = getTreeRef(path); // Get a copy of the subtree for this node
        const titleOptions = getTreeTitle(path);

        const nodeKey = selectionPath?.[depthIndex + 1]; // Tree Accessor key for the given node
        const isLeaf = Array.isArray(options); // Listings are stored in an array instead of an object

        const selectedValue: IPlanDropdown.OptionType = {
            label: isLeaf
                ? listingLabels?.[nodeKey]?.label
                : getLabel(nodeKey).categoryName,
            value: nodeKey,
        };

        // listingLabels is now an object containing label and is_recommended
        const optionsToRender: IPlanDropdown.OptionType[] = (
            Object[isLeaf ? "values" : "keys"](options) as string[]
        ).map(p => ({
            label: isLeaf
                ? listingLabels?.[p]?.label
                : getLabel(p).categoryName,
            value: p,
            is_recommended: listingLabels?.[p]?.is_recommended,
        }));

        const { categoryName, categoryTitle } = titleOptions || {};

        return {
            isLeaf,
            value: (selectedValue?.value && selectedValue) || undefined,
            options: optionsToRender,
            categoryName,
            categoryTitle,
        };
    };

    const contextValue: IPlanDropdownContextValue = {
        hideComponent,
        selectionPath,
        rootKey,
        selectedEvent: selectedEventUuid,
        handleChange,
        getDropdownSelectorsProps,
        selectionPathToRender,
        loading,
        planMetadata,
    };

    return (
        <PlanDropdownContext.Provider value={contextValue}>
            {children}
        </PlanDropdownContext.Provider>
    );
}

PlanDropdownContextProvider.defaultProps = {
    isOpenedAsEventModal: undefined,
};

export function usePlanDropdownContext() {
    return useContext(PlanDropdownContext);
}
